'use client'

import { useCallback, useEffect, useState } from 'react'
import { Product, SizeGuide, User, Listing, Order } from '@/api'
import { StateInfoModal } from './modals/state-info-modal'
import { SoldOutModal } from './modals/sold-out-modal'
import InfoIcon from '@/app/_components/icons/InfoIcon'
import { SizeGuideModal } from './modals/size-guide-modal'
import { LikeButton } from './actions/LikeButton'
import { AddToBagButton } from './actions/AddToBagButton'
import { ListingImageGallery } from './gallery/ListingImageGallery'
import { ListingInfo } from './info/ListingInfo'
import { CheckedItems } from './checked-items/CheckedItems'
import { RecommendedItems } from './recommend-items/RecommendedItems'
import ImageModal from '@/app/_components/modals/ImageModal'
import { useListingData } from './hooks/useListingData'
import { useListingModals } from './hooks/useListingModals'
import { useGTMTracking } from '@/hooks/useGTMTracking'
import LayoutBreadcrumb from '@/app/_components/layout/breadcrumb'
import { createListingBreadcrumbs } from '@/lib/breadcrumbs'
import { notFound } from 'next/navigation'
import { addCheckedItem } from '@/lib/checkedItems'
import '../listings.css'

export const ListingDetail = () => {
  const { trackEvent } = useGTMTracking()

  const {
    listing,
    currentOrder,
    currentUser,
    productImages,
    listingImages,
    conditionTextList,
    sizeGuides,
    isSoldOut,
    isAuthenticated,
    isLiked,
    loading,
    refetch,
  } = useListingData()

  if (!loading && !listing) {
    notFound()
  }

  const {
    stateInfoModalOpen,
    setStateInfoModalOpen,
    soldOutModalOpen,
    setSoldOutModalOpen,
    showSizeGuideModal,
    setShowSizeGuideModal,
    modalImages,
    modalCurrentIndex,
    setModalCurrentIndex,
    handleStateInfoModalOpen,
    handleSoldOut,
    handleImageModalOpen,
    handleImageModalClose,
  } = useListingModals()

  const [activeTab, setActiveTab] = useState<'recommended' | 'checked'>(
    'recommended'
  )

  const handleLikeSuccess = useCallback(() => {
    refetch()
  }, [refetch])

  // GTM tracking
  useEffect(() => {
    trackEvent(
      'view_item',
      listing?.product as Product,
      currentUser as User,
      null,
      listing as Listing
    )
  }, [trackEvent, listing, currentUser])

  useEffect(() => {
    if (listing?.uuid) {
      addCheckedItem(listing.uuid)
    }
  }, [listing?.uuid])

  return (
    <main>
      <LayoutBreadcrumb
        items={createListingBreadcrumbs(
          listing?.product?.productMaster?.sexI18n,
          listing?.product?.productMaster?.categories
        )}
      />
      <div className="px-5 lg:px-16 max-w-[1224px] mx-auto mb-[140px] lg:mb-[190px]">
        <div className="flex flex-col gap-8 lg:gap-16 lg:flex-row">
          <ListingImageGallery
            productImages={productImages}
            listingImages={listingImages}
            isSoldOut={Boolean(isSoldOut)}
            onImageClick={handleImageModalOpen}
          />

          <div className="grow">
            <div className="pb-5 lg:pb-7 border-b border-[#BFBFBF]">
              <div className="flex justify-between items-center">
                <h1 className="text-lg sm:text-2xl lg:text-3xl mr-4">
                  {listing?.product?.productMaster?.name}
                </h1>
                <LikeButton
                  listing={listing as Listing}
                  isLiked={isLiked}
                  likeCount={listing?.likeCount ?? 0}
                  disabled={!isAuthenticated}
                  onLikeSuccess={handleLikeSuccess}
                />
              </div>
              <div className="flex flex-row items-center gap-2 text-base lg:text-lg mt-4 pb-4 lg:pb-0">
                <div>
                  <span className="font-semibold">
                    {isSoldOut
                      ? '---,---'
                      : `¥${listing?.price?.toLocaleString()}`}
                  </span>
                  {!isSoldOut && (
                    <span className="font-semibold ml-1">(tax in)</span>
                  )}
                </div>
                {!isSoldOut && listing?.product?.productMaster?.price && (
                  <span className="text-sm lg:text-base text-[#727483]">
                    ¥{Math.round(listing.product.productMaster.price * 1.1).toLocaleString()}{' '}
                    (新品時の参考価格)
                  </span>
                )}
              </div>
              <div className="flex justify-end text-xs lg:text-base text-[#787878]">
                <span className="break-words max-w-[40%]">
                  STYLE#{' '}
                  {listing?.product?.productMaster?.code?.split('-')[0] ||
                    listing?.product?.productMaster?.code}
                </span>
              </div>
            </div>

            <div className="py-5 lg:py-7 border-b border-[#BFBFBF]">
              <div className="flex items-center mb-4 lg:mb-5">
                <span className="text-xs lg:text-base font-bold mr-2.5 lg:mr-3.5">
                  COLOR:
                </span>
                <div className="text-sm lg:text-base">
                  {listing?.product?.color?.name}
                </div>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <span className="text-xs lg:text-base font-bold mr-2.5 lg:mr-3.5">
                    SIZE:
                  </span>
                  <div className="text-sm lg:text-base">
                    {listing?.size?.name}
                  </div>
                </div>
                {sizeGuides && sizeGuides.length > 0 && (
                  <>
                    <span
                      className="underline text-xs lg:text-sm cursor-pointer"
                      onClick={() => setShowSizeGuideModal(true)}
                    >
                      Size Guide
                    </span>
                    <SizeGuideModal
                      setOpen={setShowSizeGuideModal}
                      open={showSizeGuideModal}
                      sizeGuide={sizeGuides as SizeGuide[]}
                    />
                  </>
                )}
              </div>
            </div>

            <div className="py-5 lg:py-7 border-b border-[#BFBFBF]">
              <label className="flex items-center justify-between text-xs lg:text-base font-bold">
                CONDITION
                <InfoIcon
                  className="fill-earthbrown cursor-pointer"
                  onClick={handleStateInfoModalOpen}
                />
              </label>
              {isSoldOut ? (
                <span className="text-sm lg:text-base text-[#AEAEAE] font-bold">
                  出品アイテムは御座いません
                </span>
              ) : (
                <div className="pt-4">
                  <div className="text-sm">{listing?.condition?.name}</div>
                </div>
              )}
            </div>

            <div className="mt-5 lg:mt-7 flex items-center gap-6">
              <AddToBagButton
                listing={listing as Listing}
                currentOrder={currentOrder as Order}
                onSoldOut={handleSoldOut}
              />
            </div>
          </div>
        </div>

        <ListingInfo
          conditionTextList={conditionTextList}
          product={listing?.product as Product}
        />

        {/* Recommended Items & Checked Items */}
        <div className="mt-[46px] lg:mt-[105px]">
          {/* Tab Navigation */}
          <div className="flex justify-center gap-4 mb-6 lg:mb-8">
            <button
              type="button"
              onClick={() => setActiveTab('recommended')}
              className={`
                px-6 py-2 lg:px-8 lg:py-3 text-xs lg:text-base font-bold transition-all
                ${
                  activeTab === 'recommended'
                    ? 'border-b-2 border-earthbrown text-earthbrown'
                    : 'text-gray-400 hover:text-gray-600'
                }
              `}
            >
              おすすめアイテム
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('checked')}
              className={`
                px-6 py-2 lg:px-8 lg:py-3 text-xs lg:text-base font-bold transition-all
                ${
                  activeTab === 'checked'
                    ? 'border-b-2 border-earthbrown text-earthbrown'
                    : 'text-gray-400 hover:text-gray-600'
                }
              `}
            >
              チェックしたアイテム
            </button>
          </div>

          {/* Content */}
          {activeTab === 'recommended' ? (
            <RecommendedItems listingUuid={listing?.uuid || ''} />
          ) : (
            <CheckedItems />
          )}
        </div>

        <StateInfoModal
          open={stateInfoModalOpen}
          setOpen={setStateInfoModalOpen}
        />
        <SoldOutModal open={soldOutModalOpen} setOpen={setSoldOutModalOpen} />

        <ImageModal
          images={modalImages}
          currentIndex={modalCurrentIndex}
          onClose={handleImageModalClose}
          onImageChange={setModalCurrentIndex}
          productImagesCount={productImages.length}
          showCurrentStatusLabels={false}
        />
      </div>
    </main>
  )
}
